import { FC, useEffect, useMemo, useRef } from 'react'
import { get } from 'lodash'
import { Controller, FieldArrayPath, useFormContext } from 'react-hook-form'

import {
  Flex,
  HStack,
  Icon,
  Input,
  SelectInput,
  Text,
  VStack,
} from '@revolut-internal/ui-kit'

import { Security } from 'modules/security/security'
import {
  AlertingMetric,
  AlertingMetricCondition,
  AlertingMetricValueType,
  AlertingPolicyCreateRequest,
} from 'types'

const VALUE_TYPE_CONFIGS: Record<
  AlertingMetricValueType,
  {
    label: string
    valueType: AlertingMetricValueType
    validate: (value: string) => string | undefined
  }
> = {
  [AlertingMetricValueType.Count]: {
    label: 'count',
    valueType: AlertingMetricValueType.Count,
    validate: value => {
      try {
        return Number(value) < 0 ? 'Must be positive or 0' : undefined
      } catch (e) {
        return 'Must be a number'
      }
    },
  },
  [AlertingMetricValueType.Duration]: {
    label: 'min',
    valueType: AlertingMetricValueType.Duration,
    validate: value => {
      try {
        if (Number(value) <= 0) {
          return 'Must be greater than 0'
        }
        if (Number(value) > Number.MAX_SAFE_INTEGER) {
          return `Must be lower than ${Number.MAX_SAFE_INTEGER}`
        }

        return undefined
      } catch (e) {
        return 'Must be a number'
      }
    },
  },
  [AlertingMetricValueType.Percentage]: {
    label: '%',
    valueType: AlertingMetricValueType.Percentage,
    validate: value => {
      try {
        return Number(value) <= 0 || Number(value) > 100
          ? 'Must be a number between 0 and 100'
          : undefined
      } catch (e) {
        return 'Must be a number'
      }
    },
  },
}
const conditionOptions = Object.values(AlertingMetricCondition).map(condition => ({
  key: condition,
  value: condition,
  label: `Is ${condition.toLowerCase()} than`,
}))

type QueueHealthPolicyThresholdConditionProps = {
  index: number
  fieldName: FieldArrayPath<AlertingPolicyCreateRequest>
  metrics?: AlertingMetric[]
}

export const QueueHealthPolicyThresholdCondition: FC<
  QueueHealthPolicyThresholdConditionProps
> = ({ fieldName, index, metrics }) => {
  const {
    control,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitted },
  } = useFormContext()

  const isSubmittedRef = useRef<boolean>(isSubmitted)
  const isReadonly = !Security.canCreatePolicy

  const valueFiledError = get(errors, `${fieldName}.${index}.value.value`)
  const selectedMetricId = watch(`${fieldName}.${index}.metricId`)

  useEffect(() => {
    isSubmittedRef.current = isSubmitted
  }, [isSubmitted])

  useEffect(() => {
    if (!isSubmittedRef.current) {
      return
    }

    trigger(`${fieldName}.${index}.value.value`)
  }, [selectedMetricId, fieldName, index, trigger])

  const selectedThresholdValueConfig = useMemo(() => {
    const metricValueType = metrics?.find(
      metric => metric.id === selectedMetricId,
    )?.valueType

    if (!metricValueType) {
      return {
        label: '',
        valueType: undefined,
        validate: undefined,
      }
    }

    return {
      ...VALUE_TYPE_CONFIGS[metricValueType],
      label: `(${VALUE_TYPE_CONFIGS[metricValueType].label})`,
    }
  }, [selectedMetricId, metrics])

  useEffect(() => {
    if (!selectedThresholdValueConfig.valueType) {
      return
    }

    setValue(`${fieldName}.${index}.value.type`, selectedThresholdValueConfig.valueType)
  }, [fieldName, index, selectedThresholdValueConfig.valueType, setValue])

  return (
    <VStack>
      <Flex gap="s-8">
        <Controller
          name={`${fieldName}.${index}.condition`}
          control={control}
          defaultValue={AlertingMetricCondition.Greater}
          render={({ field }) => (
            <SelectInput
              label="Condition"
              disabled={isReadonly}
              searchable={false}
              options={conditionOptions}
              {...field}
            />
          )}
        />
        <Controller
          name={`${fieldName}.${index}.value.value`}
          control={control}
          rules={{
            required: 'Field is required',
            validate: selectedThresholdValueConfig.validate ?? undefined,
          }}
          render={({ field }) => {
            return (
              <Input
                width="42%"
                label={`Value ${selectedThresholdValueConfig.label}`}
                type="number"
                disabled={isReadonly}
                {...field}
              />
            )
          }}
        />
      </Flex>

      {valueFiledError && typeof valueFiledError.message === 'string' && (
        <HStack space="s-8" py="s-4" px="s-16" color="danger" align="center">
          <Icon name="16/ExclamationMarkOutline" size={12} />
          <Text flex="1" variant="small" aria-live="polite">
            {valueFiledError.message}
          </Text>
        </HStack>
      )}
    </VStack>
  )
}
